/**
 * DefectsContent Component
 * Displays list of defects with filtering and search
 * FT-16: Mobile responsive pass - larger text, 44px+ touch targets, optimized info density
 */

import React, { useMemo } from 'react';
import { AlertTriangle, Wrench, Plus } from 'lucide-react';
import { Badge } from '../../../catalyst/badge';
import AttributionBadge from '../../shared/AttributionBadge';
// FT-353a: row chip flags "this defect has a linked corrective job".
import RowStatusIndicator from '../../shared/RowStatusIndicator';

// FT-209-6 pattern: correctiveJobId may be a string OR a legacy object
// (`{ id: '...' }`). Normalize before feeding to the row indicator.
const normalizeCorrectiveJobId = (defect) => {
  const raw = defect?.correctiveJobId;
  if (typeof raw === 'string') return raw;
  if (raw && typeof raw === 'object' && typeof raw.id === 'string') return raw.id;
  return null;
};

const DefectsContent = ({
  defects,
  activeTab,
  searchTerm,
  categoryFilter = 'all',
  onSearchChange,
  onRecordDefect,
  onViewDefect,
  showRepairsOnly,
  onToggleRepairsOnly,
  showAllVessels = false
}) => {
  // Filter defects based on tab, search, and type - memoized for performance
  const filteredDefects = useMemo(() => {
    const searchLower = searchTerm.toLowerCase(); // Calculate once

    return defects.filter(defect => {
      const matchesTab = activeTab === 'active'
        ? defect.status !== 'Closed'
        : defect.status === 'Closed';

      const matchesSearch = searchTerm === '' ||
        defect.description?.toLowerCase().includes(searchLower) ||
        defect.location?.toLowerCase().includes(searchLower) ||
        defect.equipment_linked?.toLowerCase().includes(searchLower);

      const matchesType = showRepairsOnly
        ? defect.record_type === 'repair'
        : true;

      // Category filter - checks equipment_category field
      const matchesCategory = categoryFilter === 'all' ||
        defect.equipment_category?.toLowerCase() === categoryFilter.toLowerCase();

      return matchesTab && matchesSearch && matchesType && matchesCategory;
    });
  }, [defects, activeTab, searchTerm, showRepairsOnly, categoryFilter]);

  const getRiskBadge = (level, isRepair) => {
    if (isRepair) {
      return <Badge color="blue">{level}</Badge>;
    }
    switch (level) {
      case 'Critical': return <Badge color="red">Critical</Badge>;
      case 'High': return <Badge color="orange">High</Badge>;
      case 'Medium': return <Badge color="yellow">Medium</Badge>;
      case 'Low': return <Badge color="blue">Low</Badge>;
      default: return <Badge>{level}</Badge>;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'Open': return <Badge color="red">Open</Badge>;
      case 'In Progress': return <Badge color="yellow">In Progress</Badge>;
      case 'Closed': return <Badge color="green">Closed</Badge>;
      default: return <Badge>{status}</Badge>;
    }
  };

  if (filteredDefects.length === 0) {
    return (
      <div className="text-center py-12 px-4">
        <AlertTriangle className="mx-auto h-12 w-12 text-gray-400" />
        {/* FT-16: Larger text on mobile */}
        <h3 className="mt-2 text-base md:text-sm font-medium text-gray-900">
          {searchTerm ? 'No defects found' : `No ${activeTab} defects`}
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          {searchTerm ? 'Try adjusting your search terms' : 'Get started by reporting a defect or logging a repair'}
        </p>
        {/* FT-16: 44px+ touch target on mobile */}
        <div className="mt-6">
          <button
            onClick={onRecordDefect}
            className="inline-flex items-center gap-2 rounded-lg bg-cyan-600 px-5 py-3 md:px-4 md:py-2 text-base md:text-sm font-medium text-white hover:bg-cyan-500 active:bg-cyan-700 transition-colors min-h-[44px]"
          >
            <Plus className="h-5 w-5 md:h-4 md:w-4" />
            Report Defect
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="-mx-6 -my-6">
      <div className="divide-y divide-gray-200">
        {filteredDefects.map((defect) => (
          <div
            key={defect.id}
            className="px-4 md:px-6 py-4 md:py-3.5 hover:bg-gray-50 active:bg-gray-50 cursor-pointer transition-colors min-h-[72px]"
            onClick={() => onViewDefect(defect)}
          >
            {/* FT-16: Mobile-optimized layout */}
            <div className="flex items-start justify-between gap-3 md:gap-4">
              {/* Left side: Icon + Content */}
              <div className="flex items-start gap-3 flex-1 min-w-0">
                {/* FT-16: Larger icon container on mobile */}
                <div className="w-12 h-12 md:w-10 md:h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                  {defect.record_type === 'repair' ? (
                    <Wrench className="h-6 w-6 md:h-5 md:w-5 text-blue-600" />
                  ) : (
                    <AlertTriangle
                      className={`h-6 w-6 md:h-5 md:w-5 ${
                        defect.risk_level === 'Critical' ? 'text-red-600' :
                        defect.risk_level === 'High' ? 'text-orange-600' :
                        defect.risk_level === 'Medium' ? 'text-yellow-600' :
                        'text-blue-600'
                      }`}
                    />
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  {/* FT-16: Larger title text on mobile */}
                  <p className="text-base md:text-sm font-medium text-gray-900 truncate">
                    {defect.description}
                  </p>

                  {/* FT-16: Larger location text on mobile */}
                  <p className="text-sm md:text-sm text-gray-500 mt-0.5 truncate">
                    {defect.location || 'Location not specified'}
                  </p>
                  {/* FT-06a: Attribution display - hidden on mobile for density */}
                  <AttributionBadge createdBy={defect.createdBy} createdAt={defect.createdAt} className="mt-1 hidden md:block" />
                </div>
              </div>

              {/* FT-16: Right side badges - stacked on mobile for touch targets */}
              <div className="flex flex-col md:flex-row items-end md:items-center gap-1.5 md:gap-2 flex-shrink-0">
                {/* Risk badge - only for defects, not repairs */}
                {defect.record_type !== 'repair' && getRiskBadge(defect.risk_level)}

                {/* Status badge */}
                {getStatusBadge(defect.status)}

                {/* FT-353a: corrective-job chip — renders only when the defect
                    has a linked corrective job. Empty state is invisible
                    (not em-dash) because the badges column is not a "status
                    column"; a placeholder would add noise on rows without a
                    job. Achieved by only mounting when jobId is present. */}
                {normalizeCorrectiveJobId(defect) && (
                  <RowStatusIndicator
                    jobId={normalizeCorrectiveJobId(defect)}
                    variant="inline"
                    size="sm"
                    testIdPrefix={`defect-list-row-${defect.id}`}
                  />
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default React.memo(DefectsContent);